import type { Getter } from 'ccstate'
import { isComputed, isState } from './guard'
import { isExportableInstances } from './instances'

type Loadable<T> = {
  state: 'loading';
} | {
  state: 'hasData';
  data: Awaited<T>;
} | {
  state: 'hasError';
  error: unknown;
};

async function peekPromise<T>(promise: Promise<T>): Promise<Loadable<T>> {
  let data: T | undefined = undefined
  let hasData = false
  let error: unknown = undefined
  let hasError = false
  promise.then(_data => {
    hasData = true
    data = _data
  }).catch(_error => {
    hasError = true
    error = _error
  })
  await new Promise<void>(resolve => resolve()) // yield control
  if (hasData) {
    return { state: 'hasData', data: data as Awaited<T> }
  } else if (hasError) {
    return { state: 'hasError', error }
  } else {
    return { state: 'loading' }
  }
}

function isPromise(obj: unknown): obj is Promise<unknown> {
  const maybePromise = obj as (Promise<unknown> | undefined)
  return obj !== null &&
    (typeof obj === 'object' || typeof obj === 'function') &&
    typeof maybePromise?.then === 'function'
}

const isPlainObj = (value: unknown) => value?.constructor === Object;

function checkCanBeJsonStringify(value: unknown, path: string[] = []): void {
  
  const fieldPath = path.length > 0 ? path.join('.') : '<root>'

  // null and undefined is allowed
  if (value === null || value === undefined) {
    return
  }

  // primitives (string, number, boolean) are allowed
  if (typeof value !== 'object' && typeof value !== 'function') {
    return
  }
  // Symbol is not allowed
  if (typeof value === 'symbol') {
    throw new Error(`Cannot JSON stringify Symbol at ${fieldPath}`)
  }

  // Arrays are allowed, recursively check elements
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      checkCanBeJsonStringify(value[i], [...path, `[${i}]`])
    }
    return
  }

  if (Boolean(value) && !isPlainObj(value)) {
    throw new Error(`Cannot JSON stringify non-plain object at ${fieldPath}, it is ${Object.getPrototypeOf(value)}`)
  }

  if (value && typeof value === 'object') {
    for (const key of Object.keys(value)) {
      checkCanBeJsonStringify((value as Record<string, unknown>)[key], [...path, key])
    } 
  }
}

async function takeStateSnapshotImpl(get: Getter, stateObj: object) {
  if (typeof stateObj !== 'object') {
    return stateObj
  } else if (stateObj === null) {
    return null
  } else if (Array.isArray(stateObj)) {
    return stateObj
  } else if (isState(stateObj)) {
    return get(stateObj)
  } else if (isComputed(stateObj)) {
    const maybePromise = get(stateObj)
    if (isPromise(maybePromise)) {
      return await peekPromise(maybePromise)
    } else {
      return { state: 'hasData', data: maybePromise }
    }
  } else if (isExportableInstances(stateObj)) {
    return await takeStateSnapshotImpl(get, stateObj.__exportInstances())
  } else {
    const snapshot: Record<string, unknown> = {}
    for(const [k, v] of Object.entries(stateObj)) {
      snapshot[k] = await takeStateSnapshotImpl(get, v)
    }
    return snapshot
  }
}

export async function takeStateSnapshot(get: Getter, stateObj: object) {
  const dst = await takeStateSnapshotImpl(get, stateObj)
  checkCanBeJsonStringify(dst)
  return dst
}